import Icon from '@material-ui/core/Icon'
import IconButton from '@material-ui/core/IconButton'
import Tooltip from '@material-ui/core/Tooltip'
import { MUIDataTableColumnDef } from 'mui-datatables'
import React, { Fragment, useState } from 'react'
import { PreventionCategory_Minimal } from '../../../../../graphql/fragments/Prevention/PreventionCategory/typings/PreventionCategory_Minimal'
import { EnumUser } from '../../../../../graphql/typings/global_types'
import DataTable from '../../../../components/DataTable'
import { ConfirmDialog } from '../../../../components/Dialog'
import Panel from '../../../../components/Panel'
import { useErrorService } from '../../../../helpers/errors/ErrorService'
import { useAuthUser } from '../../../../hooks/useAuth'
import { useCommonDataLoadedExisting } from '../../../../hooks/useCommonData'
import { useDeletePreventionCategory } from '../../../../hooks/useMutations'
import useSnackBar from '../../../../hooks/useSnackBar'
import PreventionCategoryDialog from './PreventionCategoryDialog'

type DialogState =
    | {
          open: true
          preventionCategory: PreventionCategory_Minimal
      }
    | { open: false }

type FormattedCategory = {
    id: string
    title: string
    description: string
}

const AEAPreventionCategoriesList = () => {
    const { preventionCategories } = useCommonDataLoadedExisting()
    const { errorAlert } = useErrorService()
    const auth = useAuthUser()
    const isAdmin = auth.user.userType === EnumUser.admin
    const { openSnack } = useSnackBar()
    const deletePreventionCategory = useDeletePreventionCategory()

    const [openAddDialog, setOpenAddDialog] = useState<boolean>(false)
    const [openEditDialog, setOpenEditDialog] = useState<DialogState>({ open: false })
    const [openDeleteDialog, setOpenDeleteDialog] = useState<DialogState>({
        open: false,
    })
    const [deleteLoading, setDeleteLoading] = useState<boolean>(false)

    const columns: MUIDataTableColumnDef[] = [
        {
            name: 'title',
            label: 'Nom',
        },
        {
            name: 'description',
            label: 'Description',
        },
    ]

    const addFeatureData = {
        addHandler: () => {
            setOpenAddDialog(true)
        },
        tooltip: `Ajout d'une catégorie de prévention`,
    }

    const onCreated = (preventionCategory: PreventionCategory_Minimal) => {
        openSnack({
            type: 'success',
            message: `Catégorie ${preventionCategory.title} créée avec succès !`,
        })
    }

    const onEdited = (preventionCategory: PreventionCategory_Minimal) => {
        openSnack({
            message: 'Catégorie modifiée avec succès !',
            type: 'success',
        })
    }

    const onEditClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        preventionCategory: PreventionCategory_Minimal
    ) => {
        event.stopPropagation()
        setOpenEditDialog({ open: true, preventionCategory })
    }

    const onDeleteClick = (
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
        preventionCategory: PreventionCategory_Minimal
    ) => {
        event.stopPropagation()
        setOpenDeleteDialog({ open: true, preventionCategory })
    }

    const onDeleteConfirmed = async () => {
        if (!openDeleteDialog.open) return

        setDeleteLoading(true)

        try {
            const preventionCategory = openDeleteDialog.preventionCategory

            const response = await deletePreventionCategory.run({
                id: preventionCategory.id,
            })

            if (response.errors.length > 0) {
                throw 'Une erreur est survenue pendant la suppression de la catégorie.'
            } else {
                openSnack({
                    message: 'Catégorie supprimée avec succès !',
                    type: 'success',
                })
            }
        } catch (error) {
            errorAlert(error)
        } finally {
            setDeleteLoading(false)
            setOpenDeleteDialog({ open: false })
        }
    }

    const actionsColumn = (dataIndex: number, rowIndex: number) => {
        if (!isAdmin) return null

        const preventionCategory = preventionCategories[dataIndex]

        return (
            <div className="flex items-center">
                <div className="flex-grow"></div>
                <Tooltip title="Modifier la catégorie">
                    <IconButton onClick={(event) => onEditClick(event, preventionCategory)}>
                        <Icon>edit</Icon>
                    </IconButton>
                </Tooltip>
                <Tooltip title="Supprimer la catégorie">
                    <IconButton onClick={(event) => onDeleteClick(event, preventionCategory)}>
                        <Icon>delete</Icon>
                    </IconButton>
                </Tooltip>
            </div>
        )
    }

    const formattedCategories: FormattedCategory[] = preventionCategories.map((category) => {
        return {
            ...category,
            description: category.description || '-',
        }
    })

    return (
        <Fragment>
            <Panel>
                <DataTable<FormattedCategory>
                    title={'Catégories de prévention'}
                    data={formattedCategories}
                    columns={columns}
                    showSearch={true}
                    addFeature={addFeatureData}
                    actionsColumn={actionsColumn}
                    options={{
                        sortOrder: {
                            name: 'title',
                            direction: 'asc',
                        },
                    }}
                />
            </Panel>
            <PreventionCategoryDialog
                onDone={onCreated}
                open={openAddDialog}
                closeDialog={() => setOpenAddDialog(false)}
            />
            {openEditDialog.open && (
                <PreventionCategoryDialog
                    onDone={onEdited}
                    open={openEditDialog.open}
                    closeDialog={() => setOpenEditDialog({ open: false })}
                    preventionCategory={openEditDialog.preventionCategory}
                />
            )}
            {openDeleteDialog.open && (
                <ConfirmDialog
                    open={openDeleteDialog.open}
                    onCancelled={() => setOpenDeleteDialog({ open: false })}
                    onConfirmed={onDeleteConfirmed}
                    title="Suppression d'une catégorie de prévention"
                    text={`Voulez-vous vraiment supprimer la catégorie "${openDeleteDialog.preventionCategory.title}" ? Tous les locaux ayant cette catégorie verront leur prévention supprimée.`}
                    loading={deleteLoading}
                />
            )}
        </Fragment>
    )
}

export default AEAPreventionCategoriesList
